import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { User, Mail, Shield, Briefcase, Key, Save } from 'lucide-react';

const Profile = () => {
  const userString = localStorage.getItem('portalUser');
  const user = userString ? JSON.parse(userString) : null;
  const isAdmin = localStorage.getItem('admin') || (user && user.role === 'Admin');

  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return Swal.fire('Error', 'New passwords do not match', 'error');
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('adminToken');
      await axios.put(window.API_URL + '/api/portal/auth/change-password', 
        { currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      Swal.fire({ icon: 'success', title: 'Success', text: 'Password changed successfully', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to change password', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">My Profile</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Profile Info */}
        <div className="md:col-span-1 space-y-6">
          <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-200 text-center flex flex-col items-center">
            <div className="w-24 h-24 bg-[#f06600]/20 text-[#f06600] rounded-full flex items-center justify-center text-4xl font-bold mb-4 border-4 border-purple-500/30">
              {user?.fullName?.charAt(0) || 'U'}
            </div>
            <h3 className="text-xl font-bold text-gray-900">{user?.fullName || 'User Name'}</h3>
            <span className={`mt-2 px-3 py-1 rounded-full text-xs font-bold ${
              user?.role === 'Admin' ? 'bg-red-500/20 text-red-400' :
              user?.role === 'Intern' ? 'bg-orange-500/10 text-[#f06600]' : 'bg-blue-500/20 text-blue-400'
            }`}>
              {user?.role || 'Role'}
            </span>
            <p className="text-gray-600 text-sm mt-4">{user?.status || 'Active'}</p>
          </div>

          <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-200 space-y-4">
            <div className="flex items-center text-gray-700">
              <Mail className="w-5 h-5 mr-3 text-[#f06600]" />
              <div className="overflow-hidden text-ellipsis">{user?.email || 'email@example.com'}</div>
            </div>
            {user?.fieldOrDepartment && (
              <div className="flex items-center text-gray-700">
                <Briefcase className="w-5 h-5 mr-3 text-[#f06600]" />
                <div>{user.fieldOrDepartment}</div>
              </div>
            )}
            <div className="flex items-center text-gray-700">
              <Shield className="w-5 h-5 mr-3 text-[#f06600]" />
              <div>ID: {user?.id || '---'}</div>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="md:col-span-2">
          <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-200 h-full">
            <h3 className="text-xl font-bold text-gray-900 flex items-center mb-6">
              <Key className="w-5 h-5 mr-2 text-[#f06600]" /> Change Password
            </h3>
            
            {isAdmin ? (
              <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 p-4 rounded-lg">
                Master Admin password changes are currently managed through environment variables or secure terminal access.
              </div>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Current Password</label>
                  <input 
                    type="password" 
                    required 
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none" 
                    value={passwordData.currentPassword} 
                    onChange={e => setPasswordData({...passwordData, currentPassword: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">New Password</label>
                  <input 
                    type="password" 
                    required 
                    minLength={6}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none" 
                    value={passwordData.newPassword} 
                    onChange={e => setPasswordData({...passwordData, newPassword: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Confirm New Password</label>
                  <input 
                    type="password" 
                    required 
                    minLength={6}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none" 
                    value={passwordData.confirmPassword} 
                    onChange={e => setPasswordData({...passwordData, confirmPassword: e.target.value})} 
                  />
                </div>
                <div className="pt-4">
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className={`flex items-center justify-center w-full md:w-auto px-6 py-2 rounded-lg font-medium transition-colors ${
                      isSubmitting ? 'bg-gray-600 text-gray-600 cursor-not-allowed' : 'bg-[#f06600] hover:bg-[#d05800] text-gray-900'
                    }`}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
